import { createReducer } from '@reduxjs/toolkit';

import { LobbyRoom, MyTable, RecentTable } from '../types';
import {
	activeUsersFailure,
	activeUsersRequest,
	activeUsersSuccess,
	clearCreateTableError,
	createSpecialTableFailure,
	createSpecialTableRequest,
	createSpecialTableSuccess,
	lobbyRoomsFailure,
	lobbyRoomsRequest,
	lobbyRoomsSuccess,
	myTablesFailure,
	myTablesRequest,
	myTablesSuccess,
} from './lobbyActions';

export type LobbyState = {
	loading: boolean;
	error?: string;
	/** The regular rooms; the VIP room is kept apart in `vipRoom`. */
	items: LobbyRoom[];
	vipRoom: LobbyRoom | null;
	total?: number;
	loaded: boolean;
	lastFetched: number | null;
	activeUsersCount?: number;
	activeUsersLoading?: boolean;
	activeUsersError?: string;
	myTablesLoading?: boolean;
	myTablesError?: string;
	myTablesItems: MyTable[];
	/** Tables the player left recently; never one that is also in `myTablesItems`. */
	myRecentTables: RecentTable[];
	createdTableId: string | number | null;
	createdTableLegacyRoomId?: number;
	createTableLoading: boolean;
	createTableError: string | null;
};

const initialState: LobbyState = {
	loading: false,
	items: [],
	vipRoom: null,
	total: 0,
	loaded: false,
	lastFetched: null,
	activeUsersCount: undefined,
	activeUsersLoading: false,
	activeUsersError: undefined,
	myTablesLoading: false,
	myTablesError: undefined,
	myTablesItems: [],
	myRecentTables: [],
	createdTableId: null,
	createdTableLegacyRoomId: undefined,
	createTableLoading: false,
	createTableError: null,
};

export const lobbyReducer = createReducer(initialState, b => {
	b.addCase(lobbyRoomsRequest, (s, _a) => {
		s.loading = true;
		s.error = undefined;
		// s.lastQuery = { start: a.payload.start ?? 0, limit: a.payload.limit ?? 20 };
	});
	b.addCase(lobbyRoomsSuccess, (s, a) => {
		s.loading = false;
		s.items = a.payload.items;
		s.vipRoom = a.payload.vipRoom;
		s.total = a.payload.total;
		s.loaded = true;
		s.lastFetched = Date.now();
	});
	b.addCase(lobbyRoomsFailure, (s, a) => {
		s.loading = false;
		s.error = a.payload;
	});
	b.addCase(activeUsersRequest, s => {
		s.activeUsersLoading = true;
		s.activeUsersError = undefined;
	});
	b.addCase(activeUsersSuccess, (s, a) => {
		s.activeUsersLoading = false;
		s.activeUsersCount = a.payload.count;
	});
	b.addCase(activeUsersFailure, (s, a) => {
		s.activeUsersLoading = false;
		s.activeUsersError = a.payload;
	});
	b.addCase(myTablesRequest, s => {
		s.myTablesLoading = true;
		s.myTablesError = undefined;
	});
	b.addCase(myTablesSuccess, (s, a) => {
		s.myTablesLoading = false;
		s.myTablesItems = a.payload.items;
		s.myRecentTables = a.payload.recent;
	});
	b.addCase(myTablesFailure, (s, a) => {
		s.myTablesLoading = false;
		s.myTablesError = a.payload;
	});

	b.addCase(createSpecialTableRequest, state => {
		state.createTableLoading = true;
		state.createTableError = null;
		state.createdTableId = null;
		state.createdTableLegacyRoomId = undefined;
	});
	b.addCase(createSpecialTableSuccess, (state, action) => {
		state.createTableLoading = false;
		state.createdTableId = action.payload.tableId;
		state.createdTableLegacyRoomId = action.payload.legacyRoomId;
		state.createTableError = null;
	});
	b.addCase(createSpecialTableFailure, (state, action) => {
		state.createTableLoading = false;
		state.createTableError = action.payload;
		state.createdTableId = null;
		state.createdTableLegacyRoomId = undefined;
	});
	b.addCase(clearCreateTableError, state => {
		state.createTableError = null;
	});
});
